import { CommonModule } from '@angular/common';
import { FALLBACK_INTERVIEW_LANGUAGES, InterviewLanguage } from '@edtech-ui/constants/interview-language.constant';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AIScreeningService } from '../../services/ai-Screening';

@Component({
  selector: 'app-resume-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './resume-upload.component.html',
  styleUrl: './resume-upload.component.scss'
})
export class ResumeUploadComponent implements OnInit {
  candidateId: string | null = null;
  sessionId: string | null = null;
  screeningId: string | null = null;
  organizationName: string = 'AI Interview Platform';
  logoUrl: string | null = null;
  selectedFile: File | null = null;
  isLoading = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private aiScreeningService: AIScreeningService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.candidateId = params.get('candidateId');
      this.sessionId = params.get('sessionId');
      this.screeningId = params.get('screeningId');
    });

    const cachedName = sessionStorage.getItem('organization_name');
    if (cachedName) {
      this.organizationName = cachedName;
    }
    const cachedLogo = sessionStorage.getItem('logo_url');
    if (cachedLogo) {
      this.logoUrl = cachedLogo;
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        this.toastr.error('File size should not exceed 5MB.');
        event.target.value = '';
        return;
      }

      const allowedExtensions = ['pdf', 'doc', 'docx'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (file.type === 'application/pdf' || (fileExtension && allowedExtensions.includes(fileExtension))) {
        this.selectedFile = file;
      } else {
        this.toastr.error('Please upload a PDF or DOC/DOCX file.');
        event.target.value = '';
      }
    }
  }

  showLanguageModal = false;
  languages: InterviewLanguage[] = [];
  selectedLanguage = 'en';

  continueToInterview() {
    if (this.selectedFile && this.candidateId && this.screeningId && this.sessionId) {
      this.isLoading = true;
      
      this.aiScreeningService.uploadSessionResume(this.sessionId, this.selectedFile)
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            this.cdr.detectChanges();
            if (response && (response.Success || response.success)) {
              const resumeText = response.Data?.resume_text || response.data?.resume_text;
              const resumeUrl = response.Data?.resume_url || response.data?.resume_url;
              if (resumeText) {
                sessionStorage.setItem('resume_text', resumeText);
              }
              if (resumeUrl) {
                sessionStorage.setItem('resume_url', resumeUrl);
                localStorage.setItem('resume_url', resumeUrl); // To support the user's manual change
              }
              this.toastr.success(response.Message || response.message || 'Resume uploaded successfully!');
              this.handlePostUploadNavigation();
            } else {
              this.toastr.error(response?.Message || response?.message || 'Failed to upload resume. Please try again.');
            }
          },
          error: (err) => {
            this.isLoading = false;
            this.cdr.detectChanges();
            this.toastr.error(err?.error?.Message || err?.error?.message || 'An error occurred while uploading resume.');
            console.error(err);
          }
        });
    } else {
      this.handlePostUploadNavigation();
    }
  }

  skipAndContinue() {
    this.handlePostUploadNavigation();
  }

  handlePostUploadNavigation() {
    const enableMultilanguageRaw = sessionStorage.getItem('enable_multilanguage');
    if (enableMultilanguageRaw === 'true') {
      this.fetchLanguagesAndShowModal();
    } else if (enableMultilanguageRaw === 'false') {
      this.navigateNext();
    } else {
      // If setting is not cached in sessionStorage (e.g. page refresh or direct URL access),
      // dynamically fetch the round details from the backend to verify the setting.
      if (this.screeningId) {
        this.isLoading = true;
        const accessCode = sessionStorage.getItem('screening_access_code');
        const request$ = accessCode
          ? this.aiScreeningService.getPublicScreeningRoundDetails(accessCode, this.screeningId)
          : this.aiScreeningService.getScreeningRoundDetails(this.screeningId);

        request$.subscribe({
          next: (res) => {
            this.isLoading = false;
            const round = (res as any)?.Data?.round || res?.data?.round;
            const enableMultilanguage = (round as any)?.enable_multilanguage;
            sessionStorage.setItem('enable_multilanguage', enableMultilanguage ? 'true' : 'false');
            if (enableMultilanguage) {
              this.fetchLanguagesAndShowModal();
            } else {
              this.navigateNext();
            }
          },
          error: (err) => {
            this.isLoading = false;
            console.error('Error fetching round details:', err);
            this.navigateNext();
          }
        });
      } else {
        this.navigateNext();
      }
    }
  }

  fetchLanguagesAndShowModal() {
    this.isLoading = true;
    this.aiScreeningService.getLanguages().subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res && res.Success && res.Data) {
          this.languages = res.Data;
        } else if (res && res.success && res.data) {
          this.languages = res.data;
        } else {
          // Fallback list
          this.languages = [...FALLBACK_INTERVIEW_LANGUAGES];
        }
        this.selectedLanguage = 'en';
        this.showLanguageModal = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error fetching languages:', err);
        // Fallback list on error
        this.languages = [...FALLBACK_INTERVIEW_LANGUAGES];
        this.selectedLanguage = 'en';
        this.showLanguageModal = true;
        this.cdr.detectChanges();
      }
    });
  }

  selectLanguage(code: string) {
    this.selectedLanguage = code;
  }

  confirmLanguageSelection() {
    sessionStorage.setItem('preferred_interview_language', this.selectedLanguage);
    this.showLanguageModal = false;
    this.navigateNext();
  }

  navigateNext() {
    if (this.candidateId && this.sessionId && this.screeningId) {
      this.router.navigate([
        '/interview-session',
        this.candidateId,
        this.sessionId,
        this.screeningId
      ]);
    } else {
      this.toastr.error('Missing session details.');
    }
  }
}

